<template>
    <div id="mapSearch">
        <USPBanner v-if="uspBannerVisible" />

        <Teaser :backgroundUrl="staticTeaserBackground" :gift-box-banner-visible="searchResult.giftBoxBannerVisible">
            <DetailsTeaser :title="$t('MapSearch.title')" :subtitle="$t('MapSearch.subtitle')" />
        </Teaser>
        <div class="search-content container main">
            <SearchBar :filterOptions="filterOptions" :showDateSearchFields="true" @submit="isLoading = true" />

            <SectionHeader v-if="!$route.query.q" class="mt-3">
                {{ $t('MapSearch.header.noTerm') }}
            </SectionHeader>
            <SectionHeader v-else class="mt-3">
                {{ $t('MapSearch.header', { query: $route.query.q }) }}
            </SectionHeader>

            <HotelMap :markers="markers" class="map" :skipGdprCheck="true">
                <div v-show="isLoading" class="map-overlay text-center">
                    <IconSvg filepath="spinner.svg"></IconSvg>
                </div>
                <div v-show="isEmpty" class="map-overlay text-center">
                    <div class="no-data">
                        <p>
                            {{ $t('MapSearch.text.noResults') }}
                        </p>
                        <button class="btn btn-primary" @click="resetFilterAndQuery">
                            {{ $t('SearchBar.dropdown.button.reset') }}
                        </button>
                    </div>
                </div>
            </HotelMap>
        </div>
    </div>
</template>

<script>
import Teaser from '@/components/teasers/Teaser';
import DetailsTeaser from '@/components/teasers/DetailsTeaser';
import SectionHeader from '@/components/SectionHeader';
import getHead from '@/mixins/getHead';
import SearchBar from '@/components/search/SearchBar';
import HotelMap from '@/components/map/HotelMap';
import Marker from '@/models/Marker';
import searchable from '@/mixins/searchable';
import advertisable from '@/mixins/advertisable';
import fetchResource from '@/utils/fetchResource';

export default {
    name: 'mapSearch',
    layout: ({ layout }) => layout,
    mixins: [getHead, searchable, advertisable('searchResult')],
    head() {
        return this.getHead({
            pageTitle: this.title,
            noindex: true
        });
    },
    components: {
        Teaser,
        DetailsTeaser,
        SectionHeader,
        SearchBar,
        HotelMap
    },
    async fetch(context) {
        const { store, query: queryParams } = context;
        const filterOptions =
            typeof queryParams.filterOptions === 'string'
                ? [queryParams.filterOptions]
                : queryParams.filterOptions || [];

        await fetchResource(
            context,
            store.dispatch('postMapSearch', {
                query: queryParams.q,
                filterOptions,
                arrivalDateRange: queryParams.from
                    ? {
                        from: queryParams.from,
                        until: queryParams.until
                    }
                    : null
            })
        );
    },
    asyncData() {
        return {
            isLoading: false
        };
    },
    computed: {
        staticTeaserBackground() {
            if (this.$store.state.webP) {
                return require('@/assets/webp/teasers/teaser_search-1440x560.webp');
            }
            return require('@/assets/teasers/teaser_search-1440x560.jpg');
        },
        resultItems() {
            return this.searchResult.result || [];
        },
        title() {
            return this.searchResult.query
                ? this.$t('MapSearch.header', { query: this.searchResult.query })
                : this.$t('MapSearch.header.noTerm');
        },
        markers() {
            return this.resultItems.map(result => {
                const [lat, lng] = result.latLon;
                return new Marker(lat, lng, {
                    ...result,
                    text: result.productText,
                    link: {
                        productId: result.productId,
                        webnames: [result.hotelWebname, result.productWebname]
                    }
                });
            });
        },
        isEmpty() {
            return !this.resultItems.length;
        }
    },
    methods: {
        resetFilterAndQuery() {
            this.$router.push({
                query: {
                    ...this.$route.query,
                    q: '',
                    filterOptions: [],
                    'from': undefined,
                    'until': undefined,
                },
                hash: '#searchBar'
            });
        },
        sendGTMEvent() {
            this.$gtm && this.$gtm.pushPageView('mapSearch');
        }
    },
    watch: {
        searchResult: {
            handler() {
                this.$nextTick(() => {
                    this.isLoading = false;
                });
            },
            immediate: true
        }
    },
    beforeMount() {
        this.sendGTMEvent();
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/bootstrap/variables' as bsvars;

#mapSearch {
    #map {
        position: relative;
    }

    .map-overlay {
        z-index: bsvars.$zindex-dropdown - 1;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        position: absolute;
        background: rgba(255, 255, 255, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;

        .icon-spinner {
            font-size: 50px;
        }

        .no-data {
            background: var(--white);
            padding: 15px;
            box-shadow: 0 7px 18px 0 rgba(0, 0, 0, 0.1);
            max-width: 400px;
            margin: 0 variables.$grid-padding;
        }
    }
}
</style>
